import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useDataProvider, useNotify, Title, useGetIdentity, useTranslate, RaRecord, Identifier } from 'react-admin';
import {
  Box,
  Paper,
  Button,
  Typography,
  CircularProgress,
  Checkbox,
  alpha,
  Grid,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  SelectChangeEvent,
  IconButton,
  Popover,
  FormControlLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SettingsIcon from '@mui/icons-material/Settings';

import CustomClientFilter from './CustomClientFilter';
import { DEFAULTS, fieldCategories, MEMBER_SUMMARY_STRINGS, fieldKeyMap, DUMMY_FIELDS } from '../constants';
import { ClientDataType as Client, MemberSummaryFormState, MemberSummaryRef, TPADataType } from '../utils/DataTypes';
import {
  formatLabel,
  areConfigsEqual,
  getBlankSummaryConfig,
  toClientObject,
  getFullConfigDiff,
  type FullDiff,
  isConfigBlank,
  transformFetchedConfig,
} from './memberSummaryHelpers';
import TpaList from '../filters/tpa/TPAList';
import TpaSourceFilter from '../filters/tpa/TpaSourceFilter';
import { RollupConfiguration } from './RollupConfiguration';
import ReorderFieldsDialog from './ReorderFieldDialog';
import UnsavedChangesDialog from './UnsavedChangesDialog';

const MemberSummary = forwardRef<MemberSummaryRef, { isActive: boolean }>(({ isActive }, ref) => {
  //---Hooks---
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const theme = useTheme();
  const { identity } = useGetIdentity();

  // --- State Variables ---
  // Selection and Filter State
  const [filterType, setFilterType] = useState('client');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedTpas, setSelectedTpas] = useState<RaRecord<Identifier>[]>([]);
  const [sourceClient, setSourceClient] = useState<Client | null>(null);
  const [sourceTpa, setSourceTpa] = useState<TPADataType | null>(null);
  const [tpaList, setTpaList] = useState<RaRecord<Identifier>[]>([]);

  // Configuration State
  const [summaryConfig, setSummaryConfig] = useState<MemberSummaryFormState | null>(null);
  const [formState, setFormState] = useState<MemberSummaryFormState>({});
  const [primaryView, setPrimaryView] = useState('detailed');
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(null);
  const openSettings = Boolean(settingsAnchor);

  // UI / Loading State
  const [isLoading, setIsLoading] = useState(false);
  const [isTargetConfigLoading, setIsTargetConfigLoading] = useState(false);
  const [isTargetConfigFinalized, setIsTargetConfigFinalized] = useState(false);
  const [isCloningConfig, setIsCloningConfig] = useState(false);
  const [resetClients, setResetClients] = useState(false);
  const [hasShownMultipleTpaNote, setHasShownMultipleTpaNote] = useState(false);
  const [refreshClientsKey, setRefreshClientsKey] = useState(0);
  const [refreshTPAKey, setRefreshTPAKey] = useState(0);

  // Dialog State
  const [openOverwriteDialog, setOpenOverwriteDialog] = useState(false);
  const [openReorderDialog, setOpenReorderDialog] = useState(false);
  const [isUnsavedChangesDialogOpen, setIsUnsavedChangesDialogOpen] = useState(false);
  const [unsavedChangesResolver, setUnsavedChangesResolver] = useState<{ (choice: boolean): void } | null>(null);
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // --- Derived State & Constants ---
  const localDiff = getFullConfigDiff(summaryConfig ?? getBlankSummaryConfig(), formState);
  const emptyFullDiff: FullDiff = {
    detailed: { added: [], removed: [] },
    rollup: { groupBy: { added: [], removed: [] }, sum: { added: [], removed: [] } },
    reportViewType: { changed: false },
    paStatus: { changed: false },
    zipInvoices: { changed: false },
  };

  // Other State
  const [lastClonedConfig, setLastClonedConfig] = useState<MemberSummaryFormState | null>(null);
  const [configDiff, setConfigDiff] = useState<FullDiff>(emptyFullDiff);
  const [tempOrder, setTempOrder] = useState<string[]>([]);

  // Calculated diffs for displaying in the overwrite dialog
  const cloneDetailedAdded = configDiff.detailed.added.length;
  const cloneDetailedRemoved = configDiff.detailed.removed.length;
  const cloneGroupAdded = configDiff.rollup.groupBy.added.length;
  const cloneGroupRemoved = configDiff.rollup.groupBy.removed.length;
  const cloneSumAdded = configDiff.rollup.sum.added.length;
  const cloneSumRemoved = configDiff.rollup.sum.removed.length;
  const hasCloneDiff =
    cloneDetailedAdded + cloneDetailedRemoved + cloneGroupAdded + cloneGroupRemoved + cloneSumAdded + cloneSumRemoved >
    0;

  // Calculated diffs for local changes (used for enabling save, etc.)
  const localDetailedAdded = localDiff.detailed.added.length;
  const localDetailedRemoved = localDiff.detailed.removed.length;
  const localGroupAdded = localDiff.rollup.groupBy.added.length;
  const localGroupRemoved = localDiff.rollup.groupBy.removed.length;
  const localSumAdded = localDiff.rollup.sum.added.length;
  const localSumRemoved = localDiff.rollup.sum.removed.length;
  const hasLocalDiff =
    localDetailedAdded + localDetailedRemoved + localGroupAdded + localGroupRemoved + localSumAdded + localSumRemoved >
    0;

  // Constant options and configurations
  const delimiterOptions = [',', '/', '|', '~', '@', '#', '%', '^', '*'];
  const rollupFieldsToExclude = ['days_supply', 'quantity'];
  const DISABLED_FIELDS = ['admin_fee_type', 'admin_fee_minimum'];

  // Filtered categories for the Rollup view (excluding certain fields)
  const filteredSumFieldCategories = fieldCategories.map((category) => ({
    ...category,
    fields: category.fields?.filter((field) => !rollupFieldsToExclude.includes(field)),
    groups: category.groups?.map((group) => group.filter((field) => !rollupFieldsToExclude.includes(field))),
  }));

  // Flag derived from state to indicate if a config is currently loaded
  const isConfigLoaded = !!summaryConfig;

  // --- Helper Functions ---
  const isFieldDisabled = (fieldName: string) => {
    return DISABLED_FIELDS.includes(fieldName);
  };

  const normalizeConfig = useCallback((config: MemberSummaryFormState | null): MemberSummaryFormState => {
    if (!config) return getBlankSummaryConfig();
    return {
      detailed_fields: [...(config.detailed_fields ?? [])],
      rollup_config: {
        group_by_fields: [...(config.rollup_config?.group_by_fields ?? [])],
        sum_fields: [...(config.rollup_config?.sum_fields ?? [])],
      },
      file_type: config.file_type || 'excel',
      delimiter: config.delimiter || ',',
      pa_status_enabled: !!config.pa_status_enabled,
      zip_invoices_enabled: !!config.zip_invoices_enabled,
    };
  }, []);

  const isTargetSame = () => {
    if (filterType === 'client') {
      const savedClient = summaryConfig?.target?.client_number || summaryConfig?.client_number;
      const currentClient = selectedClient?.client_number;

      return savedClient && currentClient ? String(savedClient) === String(currentClient) : false;
    }

    if (filterType === 'tpa') {
      const savedTpas =
        summaryConfig?.target?.tpas?.map((t: any) => String(t.tpa_number)) ||
        (summaryConfig?.tpa_number ? [String(summaryConfig.tpa_number)] : []);

      const currentTpas = selectedTpas?.map((t: any) => String(t.vendor_number)) || [];

      if (savedTpas.length !== currentTpas.length) return false;
      if (savedTpas.length === 0 && currentTpas.length === 0) return true;

      const sortedSavedTpas = [...savedTpas].sort();
      const sortedCurrentTpas = [...currentTpas].sort();

      return sortedSavedTpas.every((id, index) => id === sortedCurrentTpas[index]);
    }

    return true;
  };

  const hasUnsavedChanges = useMemo(() => {
    if (!summaryConfig) return false;

    const normalizedOriginalConfig = normalizeConfig(summaryConfig);
    const configChanged = !areConfigsEqual(formState, normalizedOriginalConfig);

    const targetChanged = !isTargetSame();

    return configChanged || targetChanged;
  }, [formState, summaryConfig, selectedClient, selectedTpas, filterType, normalizeConfig]);

  const handleLoadConfiguration = useCallback(
    async (options?: { showNotification?: boolean }) => {
      const showNotify = options?.showNotification !== false;

      const isClientMode = filterType === 'client';
      const clientNumber = selectedClient?.client_number;
      const tpaNumber = selectedTpas?.[0]?.vendor_number;

      if (isClientMode && !clientNumber) return;
      if (!isClientMode && !tpaNumber) return;

      setIsTargetConfigLoading(true);
      setIsTargetConfigFinalized(false);
      setSummaryConfig(null);
      setFormState({});

      if (isClientMode && !clientNumber) {
        setIsTargetConfigLoading(false);
        notify('notifications.CONFIG_NO_CLIENT_NUMBER', { type: 'warning' });
        return;
      }

      if (!isClientMode && !tpaNumber) {
        setIsTargetConfigLoading(false);
        notify('notifications.CONFIG_NO_TPA_SELECTED', { type: 'warning' });
        return;
      }

      try {
        const response = await dataProvider.getOne('MemberSummaryConfig', {
          id: isClientMode ? clientNumber! : tpaNumber!,
          meta: {
            ...(isClientMode ? { client_number: clientNumber } : { tpa_number: tpaNumber }),
          },
        });

        const rawConfigData = response?.data;
        const extractParms = rawConfigData?.extract_parms;

        if (extractParms && Object.keys(extractParms).length > 0) {
          const uiFriendlyConfig = transformFetchedConfig(extractParms);

          const fullConfig = {
            ...uiFriendlyConfig,
            client_number: rawConfigData.client_number,
            client_name: rawConfigData.client_name,
            tpa_number: rawConfigData.tpa_number,
            tpa_name: rawConfigData.tpa_name,
          };

          setSummaryConfig(fullConfig);

          if (showNotify) {
            notify('notifications.CONFIG_LOADED', { type: 'success' });
          }
        } else {
          const blankState = getBlankSummaryConfig();
          const targetInfo = isClientMode
            ? { client_number: clientNumber, client_name: selectedClient?.client_name }
            : { tpa_number: tpaNumber, tpa_name: selectedTpas?.[0]?.vendor_name };

          setSummaryConfig({
            ...blankState,
            ...targetInfo,
          });
          if (showNotify) {
            notify('notifications.CONFIG_NO_SAVED_CONFIG', { type: 'info' });
          }
        }
      } catch (error) {
        if (showNotify) {
          notify('notifications.CONFIG_FAILED_TO_FETCH', { type: 'error' });
        }
      } finally {
        setIsTargetConfigLoading(false);
        setIsTargetConfigFinalized(true);
      }
    },
    [filterType, selectedClient, selectedTpas, notify, dataProvider]
  );

  const handleClientSelection = async (client: { client_name: string; client_number: string } | null) => {
    if (isConfigLoaded && hasUnsavedChanges) {
      const confirmLeave = await getUnsavedChangesConfirmation();

      if (!confirmLeave) return;
    }

    setSelectedClient(client ? toClientObject(client) : null);
    setSummaryConfig(null);
    setFormState({});
    setIsTargetConfigFinalized(false);
    setSourceClient(null);
    setResetClients(true);
    setTimeout(() => setResetClients(false), 100);
  };

  const handleTpaSelection = async (tpas: RaRecord<Identifier>[]) => {
    if (isConfigLoaded && hasUnsavedChanges) {
      const confirmLeave = await getUnsavedChangesConfirmation();

      if (!confirmLeave) return;
    }

    setSelectedTpas(tpas);
  };

  const handleSourceClientSelection = (client: { client_name: string; client_number: string } | null) => {
    setSourceClient(client ? toClientObject(client) : null);
  };

  const executeClone = useCallback(async () => {
    const isClientMode = filterType === 'client';
    const sourceId = isClientMode ? sourceClient?.client_number : sourceTpa?.vendor_number;

    if (!sourceId) return;

    setIsCloningConfig(true);
    try {
      const response = await dataProvider.getOne('MemberSummaryConfig', {
        id: sourceId,
        meta: {
          ...(isClientMode ? { client_number: sourceId } : { tpa_number: sourceId }),
        },
      });

      const configToClone = response?.data?.extract_parms;

      if (configToClone && Object.keys(configToClone).length > 0) {
        const transformedConfig = transformFetchedConfig(configToClone);

        setFormState(transformedConfig);
        setLastClonedConfig(transformedConfig);

        notify('', {
          message: translate('notifications.CONFIG_CLONED_READY_FOR_SAVE', {
            sourceName: sourceClient?.client_name,
            targetName: selectedClient?.client_name,
          }),
          type: 'info',
        });
      } else {
        notify('notifications.CONFIG_NO_SAVED_CONFIG', { type: 'info' });
      }
    } catch (error) {
      notify('notifications.CONFIG_FAILED_TO_FETCH', { type: 'error' });
    } finally {
      setIsCloningConfig(false);
    }
  }, [filterType, sourceClient, sourceTpa, dataProvider, notify, transformFetchedConfig]);

  const handleCloneConfiguration = useCallback(async () => {
    const isClientMode = filterType === 'client';

    if (!isTargetConfigFinalized) return;

    if (isClientMode) {
      if (!sourceClient || !selectedClient || sourceClient.client_number === selectedClient.client_number) return;
    } else {
      if (!sourceTpa || selectedTpas.length < 1) return;
      if (selectedTpas.length === 1 && sourceTpa.vendor_number === selectedTpas[0]?.vendor_number) return;
    }

    const sourceId = isClientMode ? sourceClient!.client_number : sourceTpa!.vendor_number;

    const response = await dataProvider.getOne('MemberSummaryConfig', {
      id: sourceId,
      meta: isClientMode ? { client_number: sourceClient!.client_number } : { tpa_number: sourceTpa!.vendor_number },
    });

    const configToClone = response?.data?.extract_parms || {};
    const transformed = transformFetchedConfig(configToClone);

    if (areConfigsEqual(formState, transformed)) {
      notify('notifications.CONFIG_ALREADY_CLONED_SAME', { type: 'info', autoHideDuration: 5000 });
      return;
    }

    const targetIsBlank = isConfigBlank(summaryConfig);

    const localFullDiff = getFullConfigDiff(summaryConfig ?? getBlankSummaryConfig(), formState);
    const localChangeCount =
      localFullDiff.detailed.added.length +
      localFullDiff.detailed.removed.length +
      localFullDiff.rollup.groupBy.added.length +
      localFullDiff.rollup.groupBy.removed.length +
      localFullDiff.rollup.sum.added.length +
      localFullDiff.rollup.sum.removed.length;

    const hasLocalChanges = localChangeCount > 0;

    if (!targetIsBlank || hasLocalChanges) {
      const diffFromClone = getFullConfigDiff(summaryConfig ?? getBlankSummaryConfig(), transformed);
      setConfigDiff(diffFromClone);
      setOpenOverwriteDialog(true);
    } else {
      executeClone();
    }
  }, [
    filterType,
    isTargetConfigFinalized,
    sourceClient,
    selectedClient,
    sourceTpa,
    selectedTpas,
    summaryConfig,
    formState,
    dataProvider,
    notify,
    executeClone,
  ]);

  const handleDetailedFieldToggle = useCallback((field: string, checked: boolean) => {
    setFormState((prev) => {
      const current = prev.detailed_fields ?? [];
      let updated: string[];
      if (checked) {
        updated = current.includes(field) ? current : [...current, field];
      } else {
        updated = current.filter((f) => f !== field);
      }
      return { ...prev, detailed_fields: updated };
    });
  }, []);

  const handleGroupByChange = (newGroupByFields: string[], dialogConfirmed: boolean) => {
    setFormState((prevState) => {
      const currentSumFields = prevState.rollup_config?.sum_fields || [];

      let newSumFields: string[];

      if (dialogConfirmed) {
        newSumFields = [...newGroupByFields];
      } else {
        const newSumFieldsSet = new Set([...currentSumFields, ...newGroupByFields]);
        newSumFields = Array.from(newSumFieldsSet);
      }

      return {
        ...prevState,
        rollup_config: {
          ...prevState.rollup_config,
          group_by_fields: newGroupByFields,
          sum_fields: newSumFields,
        },
      };
    });
  };

  const handleSumFieldsChange = (fieldName: string, isChecked: boolean) => {
    const hasGroupBy = (formState.rollup_config?.group_by_fields?.length ?? 0) > 0;
    if (!hasGroupBy && isChecked) {
      notify('notifications.SELECT_ROLL_UP_FIRST', { type: 'info' });
      return;
    }
    setFormState((prevState) => {
      const currentSumFields = prevState.rollup_config?.sum_fields || [];
      const newSumFields = isChecked
        ? [...currentSumFields, fieldName]
        : currentSumFields.filter((field: string) => field !== fieldName);

      return {
        ...prevState,
        rollup_config: {
          ...prevState.rollup_config,
          sum_fields: newSumFields,
        },
      };
    });
  };

  const handleDelimiterChange = (event: SelectChangeEvent) => {
    const newDelimiter = event.target.value;
    setFormState((prevState) => ({
      ...prevState,
      delimiter: newDelimiter,
    }));
  };

  const handleFileTypeChange = (event: React.MouseEvent<HTMLElement>, newFileType: string | null) => {
    if (newFileType) {
      setFormState((prevState) => ({
        ...prevState,
        file_type: newFileType,
      }));
    }
  };

  const handleSave = useCallback(async () => {
    const isClientMode = filterType === 'client';

    if (isClientMode && !selectedClient) {
      notify('notifications.CONFIG_CLIENT_OR_CONFIG_MISSING', { type: 'warning' });
      return;
    }
    if (!isClientMode && selectedTpas.length === 0) {
      notify('notifications.CONFIG_NO_TPA_SELECTED', { type: 'warning' });
      return;
    }

    if (areConfigsEqual(formState, summaryConfig)) {
      notify('notifications.CONFIG_NO_CHANGES', { type: 'info' });
      return;
    }

    if (isConfigBlank(formState)) {
      notify('notifications.CONFIG_NO_FIELDS_SELECTED', { type: 'warning' });
      return;
    }

    setIsLoading(true);
    let saveAttemptedAndSuccessful = false;

    try {
      const toBackend = (uiKey: string) => fieldKeyMap[uiKey as keyof typeof fieldKeyMap] || uiKey;
      const detailed_fields = (formState.detailed_fields ?? []).map(toBackend);
      const group_by_fields = (formState.rollup_config?.group_by_fields ?? []).map(toBackend);
      const sum_fields = (formState.rollup_config?.sum_fields ?? []).map(toBackend);

      const extract_parms = {
        detailed_fields,
        rollup_config: {
          group_by_fields,
          sum_fields,
        },
        file_type: formState.file_type || 'excel',
        delimiter: formState.file_type === 'delimited' ? formState.delimiter || ',' : null,
        pa_status_enabled: formState.pa_status_enabled ?? false,
        zip_invoices_enabled: filterType === 'tpa' ? formState.zip_invoices_enabled ?? false : null,
      };

      const base = {
        extract_parms,
        created_by: identity?.fullName || DEFAULTS.UNKNOWN_USER,
      };

      const payload = isClientMode
        ? {
            ...base,
            mode: 'client',
            target: {
              client_number: selectedClient!.client_number,
              client_name: selectedClient!.client_name,
            },
          }
        : {
            ...base,
            mode: 'tpa',
            target: {
              tpas: selectedTpas.map((t) => ({
                tpa_number: t.vendor_number,
                tpa_name: t.vendor_name,
              })),
            },
          };

      await dataProvider.create('MemberSummaryConfig', { data: payload });

      notify('notifications.CONFIG_SAVED', { type: 'success' });
      saveAttemptedAndSuccessful = true;
    } catch (e) {
      notify('notifications.CONFIG_FAILED', { type: 'error' });
    } finally {
      setIsLoading(false);
    }

    if (saveAttemptedAndSuccessful) {
      await handleLoadConfiguration({ showNotification: false });
      setRefreshClientsKey((prev) => prev + 1);
      setRefreshTPAKey((prev) => prev + 1);
    }
  }, [
    filterType,
    selectedClient,
    selectedTpas,
    formState,
    summaryConfig,
    primaryView,
    identity,
    dataProvider,
    notify,
    handleLoadConfiguration,
  ]);

  const handleDelete = async () => {
    const isClientMode = filterType === 'client';
    const id = isClientMode ? selectedClient?.client_number : selectedTpas?.[0]?.vendor_number;

    if (!id) {
      notify('No client or TPA selected to delete', { type: 'warning' });
      return;
    }

    setDeleteConfirmOpen(false);
    setIsLoading(true);

    try {
      const payload = {
        id: id,
        mode: filterType,
        client_number: isClientMode ? selectedClient!.client_number : undefined,
        tpa_number: !isClientMode ? selectedTpas[0].vendor_number : undefined,
      };

      await dataProvider.delete('MemberSummaryConfig', {
        id: id,
        previousData: payload,
      });

      notify('notifications.CONFIG_DELETED', { type: 'success' });

      setSummaryConfig(null);
      setFormState(getBlankSummaryConfig());
      setIsTargetConfigFinalized(false);

      if (isClientMode) {
        setSelectedClient(null);
      } else {
        setSelectedTpas([]);
      }
    } catch (error) {
      const errorMessage = (error as Error).message || 'Error: Could not delete configuration';
      notify(errorMessage, { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = useCallback(() => {
    const allFields = fieldCategories.flatMap((category) => {
      if (category.fields) {
        return category.fields;
      }
      if (category.groups) {
        return category.groups.flat();
      }
      return [];
    });
    setFormState((prev) => ({
      ...prev,
      detailed_fields: allFields,
    }));
  }, []);

  const handleDeselectAll = useCallback(() => {
    setFormState((prev) => ({ ...prev, detailed_fields: [] }));
  }, []);

  const handleSelectAllSumFields = useCallback(() => {
    if (!formState?.rollup_config?.group_by_fields || formState.rollup_config.group_by_fields.length === 0) {
      notify('notifications.SELECT_ROLL_UP_FIRST', { type: 'info' });
      return;
    }
    const allPossibleFields = fieldCategories.flatMap(
      (category) => category.fields || (category.groups ? category.groups.flat() : [])
    );

    const visibleFields = allPossibleFields.filter((f) => !rollupFieldsToExclude.includes(f));

    setFormState((prev) => ({
      ...prev,
      rollup_config: {
        ...(prev.rollup_config || {}),
        sum_fields: visibleFields,
      },
    }));
  }, [formState, fieldCategories, rollupFieldsToExclude]);

  const handleDeselectAllSumFields = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      rollup_config: {
        ...(prev.rollup_config || {}),
        sum_fields: prev.rollup_config?.group_by_fields || [],
      },
    }));
  }, []);

  const getUnsavedChangesConfirmation = () => {
    return new Promise<boolean>((resolve) => {
      setUnsavedChangesResolver(() => resolve);
      setIsUnsavedChangesDialogOpen(true);
    });
  };

  const handleCancelLeave = () => {
    setIsUnsavedChangesDialogOpen(false);

    if (unsavedChangesResolver) {
      unsavedChangesResolver(false);
      setUnsavedChangesResolver(null);
    }
  };

  const handleConfirmLeave = () => {
    setIsUnsavedChangesDialogOpen(false);

    if (unsavedChangesResolver) {
      unsavedChangesResolver(true);
      setUnsavedChangesResolver(null);
    }
  };

  useImperativeHandle(ref, () => ({
    hasUnsavedChanges,
    getUnsavedChangesConfirmation,
  }));

  useEffect(() => {
    setFormState(normalizeConfig(summaryConfig));
  }, [summaryConfig, normalizeConfig]);

  useEffect(() => {
    const isClientMode = filterType === 'client';

    if (isClientMode && selectedClient) {
      handleLoadConfiguration();
      setHasShownMultipleTpaNote(false);
    } else if (!isClientMode && selectedTpas.length === 1) {
      handleLoadConfiguration();
      setHasShownMultipleTpaNote(false);
    } else if (!isClientMode && selectedTpas.length === 0) {
      setSummaryConfig(null);
      setFormState({});
      setIsTargetConfigFinalized(false);
    } else if (!isClientMode && selectedTpas.length > 1) {
      const blankConfig = getBlankSummaryConfig();
      setSummaryConfig(blankConfig);
      setFormState(blankConfig);
      setIsTargetConfigFinalized(true);

      if (!hasShownMultipleTpaNote) {
        notify('notifications.CONFIG_MULTIPLE_TPA_INFO', { type: 'info' });
        setHasShownMultipleTpaNote(true);
      }
    }
  }, [selectedClient, selectedTpas, filterType, handleLoadConfiguration]);

  useEffect(() => {
    setLastClonedConfig(null);
  }, [sourceClient]);

  useEffect(() => {
    dataProvider
      .getList('Tpa', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'vendor_name', order: 'ASC' },
        filter: {},
      })
      .then(({ data }) => {
        setTpaList(data);
      })
      .catch((error) => {
        notify('notifications.TPA_FETCH_ERROR', { type: 'error' });
      });
  }, [dataProvider, notify]);

  useEffect(() => {
    setSelectedClient(null);
    setSelectedTpas([]);
    setSummaryConfig(null);
    setSourceClient(null);
    setLastClonedConfig(null);
  }, [filterType]);

  useEffect(() => {
    setPrimaryView('detailed');
  }, [
    filterType,
    selectedClient?.client_number,
    selectedTpas
      .map((t) => t.vendor_number)
      .sort()
      .join('|'),
  ]);

  return (
    <Box sx={{ margin: 'auto' }}>
      <Title title={MEMBER_SUMMARY_STRINGS.PAGE_TITLE} />
      <Box sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ flex: 1 }}>
              <InputLabel size="small">Filter By</InputLabel>
              <Select
                value={filterType}
                label="Filter By"
                onChange={async (e) => {
                  if (isConfigLoaded && hasUnsavedChanges) {
                    const confirmLeave = await getUnsavedChangesConfirmation();

                    if (!confirmLeave) return;
                  }

                  setFilterType(e.target.value);
                }}
                size="small"
              >
                <MenuItem value="client">Client</MenuItem>
                <MenuItem value="tpa">TPA</MenuItem>
              </Select>
            </FormControl>{' '}
          </Grid>
          <Grid item xs={12} md={3}>
            {' '}
            {filterType === 'client' ? (
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                <CustomClientFilter
                  selectedClient={selectedClient}
                  setSelectedClient={handleClientSelection}
                  label={MEMBER_SUMMARY_STRINGS.SELECT_TARGET_CLIENT}
                  disabled={isTargetConfigLoading || isCloningConfig}
                  reportType="Clients"
                />
              </Box>
            ) : (
              <TpaList setFunction={handleTpaSelection} reset={resetClients} value={selectedTpas} />
            )}
            {filterType === 'client' && !selectedClient && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                {MEMBER_SUMMARY_STRINGS.NO_CLIENT_SELECTED}
              </Typography>
            )}
            {filterType === 'tpa' && selectedTpas.length === 0 && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                {MEMBER_SUMMARY_STRINGS.NO_TPA_SELECTED}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ flexGrow: 1 }}>
                {filterType === 'client' ? (
                  <CustomClientFilter
                    selectedClient={sourceClient}
                    setSelectedClient={handleSourceClientSelection}
                    label={MEMBER_SUMMARY_STRINGS.SELECT_SOURCE_CLIENT}
                    disabled={!isTargetConfigFinalized || !selectedClient || isLoading}
                    reportType="Member Summary"
                    refreshKey={refreshClientsKey}
                  />
                ) : (
                  <TpaSourceFilter
                    selectedTpa={sourceTpa}
                    setSelectedTpa={setSourceTpa}
                    disabled={!selectedTpas.length}
                    label="Copy Settings From TPA"
                    refreshKey={refreshTPAKey}
                  />
                )}
              </Box>
              <Button
                onClick={handleCloneConfiguration}
                disabled={
                  !isTargetConfigFinalized ||
                  (filterType === 'client' &&
                    (!selectedClient ||
                      !sourceClient ||
                      selectedClient.client_number === sourceClient.client_number)) ||
                  (filterType === 'tpa' &&
                    (selectedTpas.length < 1 ||
                      !sourceTpa ||
                      (selectedTpas.length === 1 && selectedTpas[0].vendor_number === sourceTpa.vendor_number))) ||
                  isTargetConfigLoading ||
                  isCloningConfig ||
                  isLoading
                }
                variant="contained"
              >
                {isCloningConfig ? (
                  <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />
                ) : (
                  MEMBER_SUMMARY_STRINGS.CLONE_SETTINGS
                )}
              </Button>
            </Box>
            <Box sx={{ mt: 1 }}>
              {filterType === 'client' ? (
                <>
                  {!selectedClient && (
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                      {MEMBER_SUMMARY_STRINGS.CLONE_NEEDS_TARGET}
                    </Typography>
                  )}

                  {selectedClient && !isTargetConfigFinalized && !isTargetConfigLoading && (
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                      {MEMBER_SUMMARY_STRINGS.CLONE_NEEDS_CONFIG}
                    </Typography>
                  )}

                  {selectedClient && isTargetConfigFinalized && !sourceClient && (
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                      {MEMBER_SUMMARY_STRINGS.CLONE_OPTIONAL_SOURCE}
                    </Typography>
                  )}

                  {selectedClient && isTargetConfigFinalized && sourceClient && (
                    <>
                      {selectedClient.client_number === sourceClient.client_number ? (
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                          {MEMBER_SUMMARY_STRINGS.CLONE_SAME_CLIENT_ERROR}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.primary"
                          sx={{ display: 'block', fontStyle: 'italic' }}
                        >
                          {MEMBER_SUMMARY_STRINGS.CLONE_READY_MESSAGE(
                            sourceClient.client_name,
                            selectedClient.client_name
                          )}
                        </Typography>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  {selectedTpas.length === 0 && (
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                      {MEMBER_SUMMARY_STRINGS.TPA_CLONE_NEEDS_CONFIG}
                    </Typography>
                  )}

                  {selectedTpas.length > 0 && isTargetConfigFinalized && !sourceTpa && (
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                      {MEMBER_SUMMARY_STRINGS.TPA_CLONE_OPTIONAL_SOURCE}
                    </Typography>
                  )}

                  {selectedTpas.length > 0 && isTargetConfigFinalized && sourceTpa && (
                    <>
                      {selectedTpas.length === 1 && sourceTpa.vendor_number === selectedTpas[0].vendor_number ? (
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                          {MEMBER_SUMMARY_STRINGS.CLONE_SAME_TPA_ERROR}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.primary"
                          sx={{ display: 'block', fontStyle: 'italic' }}
                        >
                          {MEMBER_SUMMARY_STRINGS.TPA_CLONE_READY_MESSAGE(
                            sourceTpa.vendor_name,
                            selectedTpas.map((t) => t.vendor_name).join(', ')
                          )}
                        </Typography>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      {summaryConfig !== null &&
        ((filterType === 'client' && selectedClient) || (filterType === 'tpa' && selectedTpas.length >= 1)) && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box>
                {/* Title */}
                <Typography
                  variant="h6"
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flexShrink: 0,
                  }}
                >
                  {filterType === 'client' && selectedClient
                    ? `Configure Summary Fields for: ${selectedClient.client_name} (${selectedClient.client_number})`
                    : filterType === 'tpa' && selectedTpas.length === 1
                    ? `Configure Summary Fields for: ${selectedTpas[0].vendor_name} (${selectedTpas[0].vendor_number})`
                    : filterType === 'tpa' && selectedTpas.length > 1
                    ? `Configure Summary Fields for: Multiple TPAs Selected`
                    : 'Configure Summary Fields'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary }}>
                    {primaryView === 'detailed'
                      ? `Detailed fields selected: ${formState.detailed_fields?.length || 0}`
                      : `Rollup fields selected: ${formState.rollup_config?.sum_fields?.length || 0}`}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={
                      primaryView === 'detailed'
                        ? (formState.detailed_fields?.length || 0) === 0
                        : (formState.rollup_config?.sum_fields?.length || 0) === 0
                    }
                    sx={{ mt: { xs: 0.5, sm: 0 } }}
                    onClick={() => {
                      setTempOrder(
                        primaryView === 'detailed'
                          ? formState.detailed_fields || []
                          : formState.rollup_config?.sum_fields || []
                      );
                      setOpenReorderDialog(true);
                    }}
                  >
                    Reorder Fields
                  </Button>
                  <Button
                    onClick={primaryView === 'detailed' ? handleSelectAll : handleSelectAllSumFields}
                    size="small"
                    variant="outlined"
                    disabled={isLoading}
                  >
                    {MEMBER_SUMMARY_STRINGS.SELECT_ALL}
                  </Button>
                  <Button
                    onClick={primaryView === 'detailed' ? handleDeselectAll : handleDeselectAllSumFields}
                    size="small"
                    variant="outlined"
                    disabled={isLoading}
                  >
                    {MEMBER_SUMMARY_STRINGS.DESELECT_ALL}
                  </Button>
                </Box>
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <Tooltip title="Additional Settings">
                  <IconButton onClick={(e) => setSettingsAnchor(e.currentTarget)}>
                    <SettingsIcon />
                  </IconButton>
                </Tooltip>
                <Popover
                  open={openSettings}
                  anchorEl={settingsAnchor}
                  onClose={() => setSettingsAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Report Options
                    </Typography>

                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={formState.pa_status_enabled || false}
                          onChange={(e) => setFormState((prev) => ({ ...prev, pa_status_enabled: e.target.checked }))}
                        />
                      }
                      label="Include PA Status"
                    />

                    {filterType === 'tpa' && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={formState.zip_invoices_enabled || false}
                            onChange={(e) =>
                              setFormState((prev) => ({ ...prev, zip_invoices_enabled: e.target.checked }))
                            }
                          />
                        }
                        label="Zip Invoices"
                      />
                    )}
                  </Box>
                </Popover>
                <Tooltip title="File Type">
                  <ToggleButtonGroup
                    value={formState.file_type || 'excel'}
                    exclusive
                    onChange={handleFileTypeChange}
                    size="small"
                  >
                    <ToggleButton value="excel">Excel</ToggleButton>
                    <ToggleButton value="delimited">Delimited</ToggleButton>
                  </ToggleButtonGroup>
                </Tooltip>

                <FormControl
                  size="small"
                  disabled={formState.file_type !== 'delimited'}
                  sx={{
                    minWidth: 80,
                  }}
                >
                  <InputLabel>By</InputLabel>
                  <Select value={formState.delimiter || ','} label="By" onChange={handleDelimiterChange}>
                    {delimiterOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <Tooltip title="Report Type">
                  <ToggleButtonGroup
                    value={primaryView}
                    exclusive
                    onChange={(e, newView) => {
                      if (newView) setPrimaryView(newView);
                    }}
                    size="small"
                    aria-label="Report Type"
                  >
                    <ToggleButton value="detailed">Detailed</ToggleButton>
                    <ToggleButton value="rollup">Rollup</ToggleButton>
                  </ToggleButtonGroup>
                </Tooltip>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={isLoading || !hasUnsavedChanges}
                    size="small"
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      MEMBER_SUMMARY_STRINGS.SAVE_CONFIGURATION
                    )}
                  </Button>
                  <Button
                    onClick={() => setDeleteConfirmOpen(true)}
                    variant="outlined"
                    color="error"
                    disabled={isLoading || isConfigBlank(summaryConfig)}
                    size="small"
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            </Box>
            <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, mt: 2, minHeight: 200 }}>
              {primaryView === 'detailed' && (
                <>
                  {fieldCategories.map((category, catIndex) => (
                    <Box
                      key={category.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        backgroundColor: catIndex % 2 !== 0 ? alpha(theme.palette.action.hover, 0.04) : 'transparent',
                        borderBottom:
                          catIndex < fieldCategories.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                        '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.06) },
                      }}
                    >
                      {/* Left label */}
                      <Box
                        sx={{
                          minWidth: 170,
                          maxWidth: 170,
                          p: 1,
                          fontWeight: 'bold',
                          borderRight: `1px solid black`,
                          flexShrink: 0,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
                          {category.name}
                        </Typography>
                      </Box>
                      <Box sx={{ flexGrow: 1, p: 1 }}>
                        {category.id === 'memberInfo' && Array.isArray(category.groups) ? (
                          category.groups.map((group, groupIndex) => (
                            <Box
                              key={groupIndex}
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                mb: groupIndex < category.groups!.length - 1 ? 1.5 : 0,
                              }}
                            >
                              {group.map((fieldName) => (
                                <Box
                                  key={`${category.id}-${fieldName}`}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    width: 110,
                                    minWidth: 110,
                                    p: '4px',
                                  }}
                                >
                                  <Checkbox
                                    checked={formState.detailed_fields?.includes(fieldName) ?? false}
                                    onChange={(e) => handleDetailedFieldToggle(fieldName, e.target.checked)}
                                    name={fieldName}
                                    disabled={isLoading || isFieldDisabled(fieldName)}
                                    size="small"
                                    sx={{ padding: '0 0 2px 0' }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      textAlign: 'center',
                                      lineHeight: 1.1,
                                      wordBreak: 'break-word',
                                      fontSize: '0.75rem',
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    {formatLabel(fieldName)}
                                  </Typography>
                                  {DUMMY_FIELDS.includes(fieldName) && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '0.7rem',
                                        color: theme.palette.text.disabled,
                                        textAlign: 'center',
                                        lineHeight: 1,
                                        fontStyle: 'italic',
                                      }}
                                    >
                                      (null)
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          ))
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 1,
                            }}
                          >
                            {category.id === 'financials' ? (
                              <>
                                {(category.fields ?? [])
                                  .filter((f) => !DUMMY_FIELDS.includes(f))
                                  .map((fieldName) => (
                                    <Box
                                      key={`${category.id}-${fieldName}`}
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        width: 110,
                                        minWidth: 110,
                                        p: '4px',
                                      }}
                                    >
                                      <Checkbox
                                        checked={formState.detailed_fields?.includes(fieldName) ?? false}
                                        onChange={(e) => handleDetailedFieldToggle(fieldName, e.target.checked)}
                                        name={fieldName}
                                        disabled={isLoading || isFieldDisabled(fieldName)}
                                        size="small"
                                        sx={{ padding: '0 0 2px 0' }}
                                      />
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          textAlign: 'center',
                                          lineHeight: 1.1,
                                          wordBreak: 'break-word',
                                          fontSize: '0.75rem',
                                          color: theme.palette.text.secondary,
                                        }}
                                      >
                                        {formatLabel(fieldName)}
                                      </Typography>
                                    </Box>
                                  ))}

                                <Divider sx={{ flexBasis: '100%', my: 1 }} />

                                {(category.fields ?? [])
                                  .filter((f) => DUMMY_FIELDS.includes(f))
                                  .map((fieldName) => (
                                    <Box
                                      key={`${category.id}-${fieldName}`}
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        width: 110,
                                        minWidth: 110,
                                        p: '4px',
                                      }}
                                    >
                                      <Checkbox
                                        checked={formState.detailed_fields?.includes(fieldName) ?? false}
                                        onChange={(e) => handleDetailedFieldToggle(fieldName, e.target.checked)}
                                        name={fieldName}
                                        disabled={isLoading || isFieldDisabled(fieldName)}
                                        size="small"
                                        sx={{ padding: '0 0 2px 0' }}
                                      />
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          textAlign: 'center',
                                          lineHeight: 1.1,
                                          wordBreak: 'break-word',
                                          fontSize: '0.75rem',
                                          color: theme.palette.text.secondary,
                                        }}
                                      >
                                        {formatLabel(fieldName)}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontSize: '0.7rem',
                                          color: theme.palette.text.disabled,
                                          textAlign: 'center',
                                          lineHeight: 1,
                                          fontStyle: 'italic',
                                        }}
                                      >
                                        (null)
                                      </Typography>
                                    </Box>
                                  ))}
                              </>
                            ) : (
                              (category.fields ?? []).map((fieldName) => (
                                <Box
                                  key={`${category.id}-${fieldName}`}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    width: 110,
                                    minWidth: 110,
                                    p: '4px',
                                  }}
                                >
                                  <Checkbox
                                    checked={formState.detailed_fields?.includes(fieldName) ?? false}
                                    onChange={(e) => handleDetailedFieldToggle(fieldName, e.target.checked)}
                                    name={fieldName}
                                    disabled={isLoading || isFieldDisabled(fieldName)}
                                    size="small"
                                    sx={{ padding: '0 0 2px 0' }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      textAlign: 'center',
                                      lineHeight: 1.1,
                                      wordBreak: 'break-word',
                                      fontSize: '0.75rem',
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    {formatLabel(fieldName)}
                                  </Typography>
                                  {DUMMY_FIELDS.includes(fieldName) && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '0.7rem',
                                        color: theme.palette.text.disabled,
                                        textAlign: 'center',
                                        lineHeight: 1,
                                        fontStyle: 'italic',
                                      }}
                                    >
                                      (null)
                                    </Typography>
                                  )}
                                </Box>
                              ))
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                </>
              )}

              {primaryView === 'rollup' && (
                <RollupConfiguration
                  sumFieldCategories={filteredSumFieldCategories}
                  filterType={filterType}
                  rollupConfig={formState.rollup_config}
                  onGroupByChange={handleGroupByChange}
                  onSumFieldsChange={handleSumFieldsChange}
                  sumDisabled={!formState.rollup_config?.group_by_fields?.length}
                  disabledFields={DISABLED_FIELDS}
                />
              )}
            </Box>
          </Paper>
        )}
      <Dialog
        open={openOverwriteDialog}
        onClose={() => {
          setOpenOverwriteDialog(false);
          notify('notifications.CONFIG_CLONE_CANCELLED', { type: 'info' });
        }}
        aria-labelledby="overwrite-dialog-title"
      >
        <DialogTitle id="overwrite-dialog-title">{MEMBER_SUMMARY_STRINGS.OVERWRITE_DIALOG_TITLE}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {MEMBER_SUMMARY_STRINGS.OVERWRITE_DIALOG_MESSAGE(
              sourceClient?.client_name || 'Source',
              selectedClient?.client_name || 'Target'
            )}
          </Typography>

          {hasCloneDiff && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {MEMBER_SUMMARY_STRINGS.DIALOG_LABELS.CHANGES_FROM_CLONE}
              </Typography>

              {(cloneDetailedAdded || cloneDetailedRemoved) > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Detailed Fields
                  </Typography>

                  {cloneDetailedAdded > 0 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <AddCircleOutlineIcon fontSize="small" color="success" sx={{ mr: 0.5 }} />
                        <Typography color="success.main" fontWeight="medium">
                          {MEMBER_SUMMARY_STRINGS.DIALOG_LABELS.ADDED}
                        </Typography>
                      </Box>
                      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1 }}>
                        {configDiff.detailed.added.map((f) => (
                          <li key={`d+a:${f}`}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {formatLabel(f)}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}

                  {cloneDetailedRemoved > 0 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <RemoveCircleOutlineIcon fontSize="small" color="error" sx={{ mr: 0.5 }} />
                        <Typography color="error.main" fontWeight="medium">
                          {MEMBER_SUMMARY_STRINGS.DIALOG_LABELS.REMOVED}
                        </Typography>
                      </Box>
                      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1 }}>
                        {configDiff.detailed.removed.map((f) => (
                          <li key={`d-r:${f}`}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {formatLabel(f)}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {(cloneGroupAdded || cloneGroupRemoved) > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Rollup — Group by
                  </Typography>

                  {cloneGroupAdded > 0 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <AddCircleOutlineIcon fontSize="small" color="success" sx={{ mr: 0.5 }} />
                        <Typography color="success.main" fontWeight="medium">
                          {MEMBER_SUMMARY_STRINGS.DIALOG_LABELS.ADDED}
                        </Typography>
                      </Box>
                      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1 }}>
                        {configDiff.rollup.groupBy.added.map((f) => (
                          <li key={`g+a:${f}`}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {formatLabel(f)}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}

                  {cloneGroupRemoved > 0 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <RemoveCircleOutlineIcon fontSize="small" color="error" sx={{ mr: 0.5 }} />
                        <Typography color="error.main" fontWeight="medium">
                          {MEMBER_SUMMARY_STRINGS.DIALOG_LABELS.REMOVED}
                        </Typography>
                      </Box>
                      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1 }}>
                        {configDiff.rollup.groupBy.removed.map((f) => (
                          <li key={`g-r:${f}`}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {formatLabel(f)}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {/* Rollup — Sum fields */}
              {(cloneSumAdded || cloneSumRemoved) > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Rollup — Sum fields
                  </Typography>

                  {cloneSumAdded > 0 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <AddCircleOutlineIcon fontSize="small" color="success" sx={{ mr: 0.5 }} />
                        <Typography color="success.main" fontWeight="medium">
                          {MEMBER_SUMMARY_STRINGS.DIALOG_LABELS.ADDED}
                        </Typography>
                      </Box>
                      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1 }}>
                        {configDiff.rollup.sum.added.map((f) => (
                          <li key={`s+a:${f}`}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {formatLabel(f)}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}

                  {cloneSumRemoved > 0 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <RemoveCircleOutlineIcon fontSize="small" color="error" sx={{ mr: 0.5 }} />
                        <Typography color="error.main" fontWeight="medium">
                          {MEMBER_SUMMARY_STRINGS.DIALOG_LABELS.REMOVED}
                        </Typography>
                      </Box>
                      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1 }}>
                        {configDiff.rollup.sum.removed.map((f) => (
                          <li key={`s-r:${f}`}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {formatLabel(f)}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Divider between clone changes and local changes */}
          {hasCloneDiff && hasLocalDiff && <Divider sx={{ my: 2 }} />}

          {/* ===== LOCAL UNSAVED CHANGES ===== */}
          {hasLocalDiff && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {MEMBER_SUMMARY_STRINGS.DIALOG_LABELS.LOCAL_UNSAVED_CHANGES}
              </Typography>

              {/* Detailed */}
              {(localDetailedAdded || localDetailedRemoved) > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Detailed Fields
                  </Typography>

                  {localDetailedAdded > 0 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <AddCircleOutlineIcon fontSize="small" color="success" sx={{ mr: 0.5 }} />
                        <Typography color="success.main" fontWeight="medium">
                          {MEMBER_SUMMARY_STRINGS.DIALOG_LABELS.ADDED}
                        </Typography>
                      </Box>
                      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1 }}>
                        {localDiff.detailed.added.map((f) => (
                          <li key={`ld+a:${f}`}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {formatLabel(f)}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}

                  {localDetailedRemoved > 0 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <RemoveCircleOutlineIcon fontSize="small" color="error" sx={{ mr: 0.5 }} />
                        <Typography color="error.main" fontWeight="medium">
                          {MEMBER_SUMMARY_STRINGS.DIALOG_LABELS.REMOVED}
                        </Typography>
                      </Box>
                      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1 }}>
                        {localDiff.detailed.removed.map((f) => (
                          <li key={`ld-r:${f}`}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {formatLabel(f)}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {/* Rollup — Group by */}
              {(localGroupAdded || localGroupRemoved) > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Rollup — Group by
                  </Typography>

                  {localGroupAdded > 0 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <AddCircleOutlineIcon fontSize="small" color="success" sx={{ mr: 0.5 }} />
                        <Typography color="success.main" fontWeight="medium">
                          {MEMBER_SUMMARY_STRINGS.DIALOG_LABELS.ADDED}
                        </Typography>
                      </Box>
                      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1 }}>
                        {localDiff.rollup.groupBy.added.map((f) => (
                          <li key={`lg+a:${f}`}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {formatLabel(f)}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}

                  {localGroupRemoved > 0 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <RemoveCircleOutlineIcon fontSize="small" color="error" sx={{ mr: 0.5 }} />
                        <Typography color="error.main" fontWeight="medium">
                          {MEMBER_SUMMARY_STRINGS.DIALOG_LABELS.REMOVED}
                        </Typography>
                      </Box>
                      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1 }}>
                        {localDiff.rollup.groupBy.removed.map((f) => (
                          <li key={`lg-r:${f}`}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {formatLabel(f)}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {/* Rollup — Sum fields */}
              {(localSumAdded || localSumRemoved) > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Rollup — Sum fields
                  </Typography>

                  {localSumAdded > 0 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <AddCircleOutlineIcon fontSize="small" color="success" sx={{ mr: 0.5 }} />
                        <Typography color="success.main" fontWeight="medium">
                          {MEMBER_SUMMARY_STRINGS.DIALOG_LABELS.ADDED}
                        </Typography>
                      </Box>
                      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1 }}>
                        {localDiff.rollup.sum.added.map((f) => (
                          <li key={`ls+a:${f}`}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {formatLabel(f)}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}

                  {localSumRemoved > 0 && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <RemoveCircleOutlineIcon fontSize="small" color="error" sx={{ mr: 0.5 }} />
                        <Typography color="error.main" fontWeight="medium">
                          {MEMBER_SUMMARY_STRINGS.DIALOG_LABELS.REMOVED}
                        </Typography>
                      </Box>
                      <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1 }}>
                        {localDiff.rollup.sum.removed.map((f) => (
                          <li key={`ls-r:${f}`}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {formatLabel(f)}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setOpenOverwriteDialog(false);
              notify('notifications.CONFIG_CLONE_CANCELLED', { type: 'info' });
            }}
          >
            {MEMBER_SUMMARY_STRINGS.CANCEL}
          </Button>
          <Button
            onClick={() => {
              setOpenOverwriteDialog(false);
              executeClone();
            }}
            variant="contained"
            color="primary"
            autoFocus
          >
            {MEMBER_SUMMARY_STRINGS.OVERWRITE_CLONE}
          </Button>
        </DialogActions>
      </Dialog>
      <ReorderFieldsDialog
        open={openReorderDialog}
        primaryView={primaryView as 'detailed' | 'rollup'}
        formState={formState}
        tempOrder={tempOrder}
        setTempOrder={setTempOrder}
        onClose={() => setOpenReorderDialog(false)}
        onSave={(newOrder) => {
          setFormState((prev) => ({
            ...prev,
            detailed_fields: primaryView === 'detailed' ? newOrder : prev.detailed_fields,
            rollup_config:
              primaryView === 'rollup' ? { ...prev.rollup_config, sum_fields: newOrder } : prev.rollup_config,
          }));
          setOpenReorderDialog(false);
        }}
      />
      <UnsavedChangesDialog
        open={isUnsavedChangesDialogOpen}
        onCancel={handleCancelLeave}
        onConfirm={handleConfirmLeave}
      />
      <Dialog
        open={isDeleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-confirm-dialog-title"
      >
        <DialogTitle id="delete-confirm-dialog-title">Delete Configuration?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this configuration? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default MemberSummary;
